import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { AppConstants } from 'src/app/commons/app-constants';

export interface TallyDefaultValidations {
  divisibleBy5: boolean;
  girthRange: boolean;
  girthMin: number;
  girthMax: number;
  lengthRange: boolean;
  lengthMin: number;
  lengthMax: number;
}

export interface TallyDefaultSettings {
  validations: TallyDefaultValidations;
  widthUom: string;
  thickUom: string;
  /** Square's own length unit — deliberately separate from roundLengthUom (typically feet vs
   *  cm; there's no reason a tenant's two tally types would share one length unit). */
  squareLengthUom: string;
  girthUom: string;
  roundLengthUom: string;
  squareDecimals: number;
  roundDecimals: number;
  squareLengthAllowance: number;
  roundLengthAllowance: number;
  girthAllowance: number;
  widthAllowance: number;
  thickAllowance: number;
  copyPrevLength: boolean;
  addToLengthEnabled: boolean;
  addToLengthValue: number | null;
  enterNavMode: string;
  /** Separate per type, not shared — Round stock naturally varies piece-to-piece far more than
   *  graded Square lumber. */
  squareSearchTolerancePercent: number;
  roundSearchTolerancePercent: number;
}

/** Tenant-level Tally Defaults (Application Settings) — the single source new tally sheets'
 *  Configuration Settings pre-populate from, and Quick Search reads for its assumed-unit
 *  fallback and match tolerance. See TallyDefaultSettingsController (tms-sb) for the backend. */
@Injectable({ providedIn: 'root' })
export class TallyDefaultSettingsService {
  private base = `${AppConstants.API_URL}api/v1/tally-default-settings`;

  constructor(private http: HttpClient) {}

  get(): Observable<TallyDefaultSettings> {
    return this.http.get<TallyDefaultSettings>(this.base);
  }

  /** 2026-09-27 — one shared request per session for the tally grids. They must WAIT for this
   *  before filling a new tally's settings: loading it in parallel meant the tally's own settings
   *  usually arrived first, so a new tally got the hardcoded 5 / 2 allowances instead of the
   *  company defaults. Emits null when it can't be loaded (offline, error) — callers then use the
   *  literal fallbacks, exactly as before. Refreshed after save(). */
  private cached$: Observable<TallyDefaultSettings | null> | null = null;

  getCached(): Observable<TallyDefaultSettings | null> {
    if (!this.cached$) {
      this.cached$ = this.get().pipe(
        tap((defs) => this.rememberOnDevice(defs)),
        catchError(() => {
          this.cached$ = null; // try again next time instead of caching the failure
          // Offline / error: the last company defaults this device saw, so a tally opened offline
          // still starts from the company's values instead of the hardcoded 5 / 2 (2026-09-27).
          return of(this.lastKnownOnDevice());
        }),
        shareReplay(1),
      );
    }
    return this.cached$;
  }

  /** Per company (tenant id from AppConstants.LAST_DEVICE_TENANT_ID) — never mixes companies. */
  private deviceKey(): string | null {
    try {
      const tenantId = localStorage.getItem(AppConstants.LAST_DEVICE_TENANT_ID);
      return tenantId ? `tms.tallyDefaults.${tenantId}` : null;
    } catch { return null; }
  }

  private rememberOnDevice(defs: TallyDefaultSettings | null): void {
    const key = this.deviceKey();
    if (!key || !defs) return;
    try { localStorage.setItem(key, JSON.stringify(defs)); } catch { /* storage full / private mode */ }
  }

  private lastKnownOnDevice(): TallyDefaultSettings | null {
    const key = this.deviceKey();
    if (!key) return null;
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as TallyDefaultSettings) : null;
    } catch { return null; }
  }

  save(dto: TallyDefaultSettings): Observable<TallyDefaultSettings> {
    return this.http.put<TallyDefaultSettings>(this.base, dto).pipe(
      tap((saved) => { this.cached$ = null; this.rememberOnDevice(saved); }),
    );
  }

  literalDefaults(): Observable<TallyDefaultSettings> {
    return this.http.get<TallyDefaultSettings>(`${this.base}/literal-defaults`);
  }
}
